import React, { useState, useEffect, useRef, useMemo } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import storage from '../utils/storage';
import { showToast } from '../utils/toastActions';
import moment from "moment";

const configOption = window && window.config;

const withNotification = (WrappedComponent) => {
    const WithNotification = (props) => {
        const [granted, setGranted] = useState(false);
        const [isConnected, setIsConnected] = useState(false);
        const stompClientRef = useRef(null);
        const [taskQueue, setTaskQueue] = useState([]); // Queue to hold tasks until permission is granted

        const userInfo = useMemo(() => {
            const storedUser = storage.get('loginUser');
            return storedUser ? JSON.parse(storedUser) : {}; // Fallback to empty object
        }, []);

        const storeId = userInfo.siteId;
        const connectionId = userInfo?.userId;

        const _body = {
            alert: 'user',
            connectionId: connectionId
        }

        useEffect(() => {
            const client = new Client({
                webSocketFactory: () => new SockJS(`${configOption?.END_POINT}/websocket`),
                reconnectDelay: 5000,
                onConnect: () => handleConnection(client),
                onDisconnect: () => setIsConnected(false),
                onStompError: handleError,
            });

            client.activate();
            stompClientRef.current = client;

            return () => {
                client.deactivate();
            };
        }, [connectionId]);

        const handleConnection = (client) => {
            setIsConnected(client.isConnected);
            sendEvent('connect', _body);
            client.subscribe(`/notification-consumer/${connectionId}/receive`, onMessageReceived);
        };

        const handleError = (error) => {
            sendEvent('error', { error });
            stompClientRef = null;
            console.error('WebSocket connection error:', error);
            showToast('error', 'Error', `WebSocket connection error: ${error}`)
        };

        const sendEvent = (eventType, body = {}) => {
            const taskNotification = { header: { eventtype: eventType, connectionId: connectionId,
                storeId: storeId, }, body };
            stompClientRef?.current?.publish({
                destination: `/notification-producer/${connectionId}/subscribe`,
                body: JSON.stringify(taskNotification),
            });
        };

        const ping = () => {
            sendEvent('ping', _body);
        };

        const onMessageReceived = (payload) => {
            const taskNotifications = JSON.parse(payload?.body);
        
            if (Array.isArray(taskNotifications)) {
                taskNotifications.forEach((task) => {
                    if (task.status != "COMPLETED") {
                        const reminderOn = moment(task.notificationDateTime, 'DD-MM-YYYY HH:mm');
        
                        // Check if the parsing was successful
                        if (!reminderOn.isValid()) {
                            console.error('Invalid reminderOn date:', task.notificationDateTime);
                            return; // Skip this task if the date is invalid
                        }
        
                        const reminderOnTime = reminderOn.toDate().getTime();
                        const now = new Date();
                        // Start of the current minute
                        const startOfMinute = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes()).getTime();
        
                        // End of the current minute
                        const endOfMinute = startOfMinute + 59 * 1000 + 999; // 59 seconds and 999 milliseconds
        
                        if (reminderOnTime >= startOfMinute && reminderOnTime <= endOfMinute) {
                            // Send notification immediately if the reminder time is now
                            if (granted) {
                                sendNotification(task);
                            } else {
                                // If permission not granted, queue the task
                                setTaskQueue((prevQueue) => [...prevQueue, task]);
                            }
                        } else if (reminderOnTime > now.getTime()) {
                            // Queue the task if the reminder time is in the future
                            setTaskQueue((prevQueue) => [...prevQueue, task]);
        
                            // Set a timer to check again when the reminder time is reached
                            const delay = reminderOnTime - now.getTime();
                            setTimeout(() => {
                                // If permission is granted, send notification when the reminder time is reached
                                if (granted) {
                                    sendNotification(task);
                                }
                            }, delay);
                        }
                    }
                });
            }
        };
        

        useEffect(() => {
            if (isConnected) {
                const interval = setInterval(() => {
                    ping();
                }, 5000);
                return () => clearInterval(interval);
            }
        }, [isConnected]);

        useEffect(() => {
            if ('Notification' in window) {
                Notification.requestPermission().then((permission) => {
                    console.log('Notification permission:', permission);
                    if (permission === 'granted') {
                        setGranted(true);
                        console.log('Notification permission granted.');
                    } else {
                        console.log('Notification permission denied.');
                        showToast('error', 'Error', 'Notification permission denied.');
                    }
                }).catch((error) => {
                    console.error('Error while requesting notification permission:', error);
                });
            }
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.register('/service-worker.js').then((registration) => {
                    console.log('Service Worker registered with scope:', registration.scope);
                }).catch((error) => {
                    console.error('Service Worker registration failed:', error);
                });
            }
        }, []);

        useEffect(() => {
            if (granted && taskQueue.length > 0) {
                taskQueue.forEach(task => {
                    console.log('Sending notification for task:', task);
                    sendNotification(task);
                });
                setTaskQueue([]); // Clear the queue after sending notifications
            }
        }, [granted, taskQueue]);

        const sendNotification = (task) => {
            console.log('Attempting to send notification:', task);
            if (granted && 'Notification' in window && Notification.permission === 'granted') {
                console.log('Sending notification...');
                navigator.serviceWorker.ready.then((registration) => {
                    registration.showNotification(task.title, {
                        body: `Event starts in ${task?.startDateTime} minutes: ${task?.body?.description}`,
                        icon: '/event-icon.png',
                        tag: task.id,
                        data: { url: task?.url },
                    }).then(() => {
                        console.log('Notification sent successfully.');
                    }).catch((error) => {
                        console.error('Error showing notification:', error);
                    });
                }).catch((error) => {
                    console.error('Service worker not ready:', error);
                });
            } else {
                console.log('Notification conditions not met.');
            }
        };

        return <WrappedComponent {...props} />;
    };

    WithNotification.displayName = `withNotification(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return WithNotification;
};

export default withNotification;
